import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useTranslation} from "react-i18next";
import {Modal, Spinner} from "@amzn/alchemy-components-react";
import {Exceptions as AuthExceptions} from "@amzn/fitjava-script-client"
import * as KatalMetrics from "@amzn/katal-metrics";
import * as CONSTANTS from 'src/common/constants';
import {isEmptyString} from 'src/common/functions';
import {useAuth} from 'src/components/auth/auth-provider';
import {logger} from "src/logger";
import initialMetricsPublisher from "src/metrics";
import {UserType} from "src/common/enums";

const cloudWatchDimensions = [
    new KatalMetrics.Metric.String('page', 'auth'),
]
// @ts-ignore
const additionalMetricsContext = new KatalMetrics.Context({cloudWatchDimensions});
const authMetricPublisher = initialMetricsPublisher.newChildActionPublisherForMethod('auth', additionalMetricsContext);


/**
 * Validates the url path.
 *
 * @param urlPath retrieved from session storage
 * @return true or false {@link boolean}.
 */
const validateUrlPath = (urlPath: string): boolean => {

    if (urlPath.startsWith(CONSTANTS.PRINCIPAL_BASELINE_PATH) || urlPath.startsWith(CONSTANTS.PERMISSION_BASELINE_PATH)) {
        return validateBaseliningUrlPath(urlPath);
    }

    const allowedUrlPath: string[] = [
        CONSTANTS.EMPTY_PATH,
        CONSTANTS.AUTH_URL,
        CONSTANTS.ONBOARD_URL,
        CONSTANTS.SINGLE_ONBOARD_URL,
        CONSTANTS.BULK_ONBOARD_URL,
        CONSTANTS.PRINCIPAL_LIST_PATH,
        CONSTANTS.PRINT_OTP_PATH,
        CONSTANTS.MFA_REGISTRATION_PATH
    ];

    return allowedUrlPath.indexOf(urlPath) > -1 || (allowedUrlPath + '/').indexOf(urlPath) > 1;
}

/**
 * Validates the baselining url path.
 *
 * @param baseliningUrlPath retrieved from session storage
 * @return true or false {@link boolean}.
 */
const validateBaseliningUrlPath = (baseliningUrlPath: string): boolean => {

    const pathSplits = baseliningUrlPath.split('/');

    // system currently supports baselining for associate, secondary and primary admins
    if (pathSplits[3] !== UserType.ASSOCIATE.toLowerCase() &&
        pathSplits[3] !== UserType.USER_ADMIN_SECONDARY.toLowerCase() &&
        pathSplits[3] !== UserType.USER_ADMIN_PRIMARY.toLowerCase() && isNaN(Number(pathSplits[3]))) {
        return false;
    }

    // verifying site length
    return pathSplits[4].length == 4;

}

const validateUrlQueryParams = (urlQueryParams: URLSearchParams): boolean => {
    let validationResult = true;

    for (const [key, value] of urlQueryParams) {
        validationResult = validationResult && validateQueryParamValue(key, value);
        if (!validationResult) {
            logger.error(`Validation of query param: ${key} failed as it contains ${value}.`)
        }
    }

    return validationResult;
}

const validateQueryParamValue = (key: string, value: string): boolean => {
    switch (key) {
        case CONSTANTS.EMPTY_VALUE:
            return true;
        case CONSTANTS.OTP:
            return CONSTANTS.NUMERIC_REGEX.test(value)
        case CONSTANTS.ERROR:
        case CONSTANTS.STATUS:
        case CONSTANTS.STATE:
            return CONSTANTS.URL_SECURE_ALPHANUMERIC_REGEX.test(value)
        case CONSTANTS.CODE:
            return CONSTANTS.UUID_REGEX.test(value)
        case CONSTANTS.VIEW_AS:
            return CONSTANTS.URL_SECURE_ALPHA_REGEX.test(value)
        default:
            return false;
    }
}

export const AuthPage = () => {
    const {t} = useTranslation();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const [authCode] = useState<string>(searchParams.get('code') || '');
    const [displaySpinner, setDisplaySpinner] = useState(true);
    const [subheader, setSubheader] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const auth = useAuth();

    useEffect(() => {
        if (isEmptyString(authCode)) {
            const fromState = location.state as { from: Location };
            const redirectFromPath = fromState?.from?.pathname || '/'; // Redirect back to where user visits or home page
            const redirectFromSearch = fromState?.from?.search || '';
            sessionStorage.setItem(CONSTANTS.SESSION_STORAGE_REDIRECT_BACK_PATH, redirectFromPath);
            sessionStorage.setItem(CONSTANTS.SESSION_STORAGE_REDIRECT_BACK_SEARCH, redirectFromSearch);

            // Set the login hint to when the request is related to the SOX url path.
            // We assume users access SOX with the permission baselining path, for example clicking an email link.
            const isSOXUrl = redirectFromPath.toLowerCase().includes(CONSTANTS.PERMISSION_BASELINE_PATH);
            const redirectUrl = auth.getAuthUrl(isSOXUrl ? CONSTANTS.FIT_SOX_IDP_LOGIN_HINT : undefined);

            logger.info('No auth code present, redirecting to FIT login page: ' + redirectUrl);
            authMetricPublisher.publishCounterMonitor('auth-code-redirect.SUCCESS', 1);
            window.location.href = redirectUrl;
            return;
        }
        auth.storeAuthCode(authCode);
        getToken();
    }, []);

    const getToken = () => {
        logger.info(`Exchanging Auth Code ${authCode} for FIT Token.`)
        authMetricPublisher.publishCounterMonitor('fit-get-token', 1);
        auth.getToken()
            .then((_) => {
                logger.info('User is authenticated');
                auth.setAuthState(true);
                authMetricPublisher.publishCounterMonitor('fit-get-token.SUCCESS', 1);
                authMetricPublisher.publishCounterMonitor('authn-flow.SUCCESS', 1);
                setSubheader(t('succeeded'));
                setDisplaySpinner(false);

                // Makes it so the retries work as expected if the browser tab is used for another authN
                const finalAuthCodeRetryCount = sessionStorage.getItem(CONSTANTS.SESSION_STORAGE_FIT_AUTH_AUTHCODE_RETRY_COUNT) || '0';
                logger.info(`Final Auth code retry count: '${finalAuthCodeRetryCount}' resetting to 0.`);
                sessionStorage.setItem(CONSTANTS.SESSION_STORAGE_FIT_AUTH_AUTHCODE_RETRY_COUNT, '0');

                // Makes it so the authN flow retries work as expected if the browser tab is used for another authN
                const finalAuthNFlowRetryCount = sessionStorage.getItem(CONSTANTS.SESSION_STORAGE_FIT_AUTHN_FLOW_RETRY_COUNT) || '0';
                logger.info(`Final AuthN flow retry count: '${finalAuthNFlowRetryCount}' resetting to 0.`);
                sessionStorage.setItem(CONSTANTS.SESSION_STORAGE_FIT_AUTHN_FLOW_RETRY_COUNT, '0');

                // Redirect back to original endpoint or the root path
                const redirectBackPath = sessionStorage.getItem(CONSTANTS.SESSION_STORAGE_REDIRECT_BACK_PATH) || "/";
                const redirectBackSearch = sessionStorage.getItem(CONSTANTS.SESSION_STORAGE_REDIRECT_BACK_SEARCH) || '';
                const searchParamsFromStorage = new URLSearchParams(redirectBackSearch);

                let redirectURI;
                if (!validateUrlPath(redirectBackPath) || !validateUrlQueryParams(searchParamsFromStorage)) {
                    authMetricPublisher.publishCounterMonitor('url-validation.FAILURE', 1);
                    logger.warn(`UrlValidation failed for redirectBackPath: ${redirectBackPath} and 
                    searchParamsFromLocalStorage: ${searchParamsFromStorage}, redirecting to base path`);
                    redirectURI = '/';
                } else {
                    authMetricPublisher.publishCounterMonitor('url-validation.SUCCESS', 1);
                    logger.info("Redirecting to " + redirectBackPath + " with parameters: " + redirectBackSearch);
                    redirectURI = `${redirectBackPath}${redirectBackSearch}`;
                }

                // Reset the redirect paths in case the browser tab is used for another authN
                sessionStorage.setItem(CONSTANTS.SESSION_STORAGE_REDIRECT_BACK_PATH, '');
                sessionStorage.setItem(CONSTANTS.SESSION_STORAGE_REDIRECT_BACK_SEARCH, '');
                window.location.href = redirectURI;
                return;
            })
            .catch((error) => {
                authMetricPublisher.publishCounterMonitor('fit-get-token.ERROR', 1);
                logger.error(`Failed to get token from FIT due to ${JSON.stringify(error)}`);

                if (error instanceof AuthExceptions.AuthenticationError) {
                    authMetricPublisher.publishCounterMonitor(`fit-get-token.ERROR.${error.kebabCaseCode()}`, 1);
                    logger.error(`Error getting the fit token related to missing session storage data. Error Code: ${error.code} Error Message: ${error.message}. Restarting the entire flow again.`);
                    retryAuthNFlow();
                } else if ('message' in error && error.message.includes('400')) {
                    authMetricPublisher.publishCounterMonitor(`fit-get-token.ERROR.client-error`, 1);
                    logger.error(`Error getting the fit token. FIT returned client error, 400 http status code. Error Message: ${error.message}. Restarting the entire flow again.`);
                    retryAuthNFlow();
                } else {
                    retryGetToken();
                }
            });
    }

    const retryAuthNFlow = () => {
        let retryCount = Number(sessionStorage.getItem(CONSTANTS.SESSION_STORAGE_FIT_AUTHN_FLOW_RETRY_COUNT) || '0');
        retryCount += 1;
        sessionStorage.setItem(CONSTANTS.SESSION_STORAGE_FIT_AUTHN_FLOW_RETRY_COUNT, retryCount.toString());
        if (retryCount <= CONSTANTS.MAX_AUTHN_FLOW_RETRIES) {
            logger.error(`Failed to AuthN retrying the flow: ${retryCount}`);
            authMetricPublisher.publishCounterMonitor('authn-flow.ERROR.retry', 1);
            setSubheader(t('retrying-attempt-of-max-attempts',
                {attempt: retryCount, maxAttempts: CONSTANTS.MAX_AUTHN_FLOW_RETRIES}));
            window.location.href = CONSTANTS.AUTH_URL;
        } else {
            logger.error(`Failed to AuthN after retrying the flow: ${retryCount} times`);
            authMetricPublisher.publishCounterMonitor('authn-flow.ERROR.retry.exhausted', 1);
            showErrorMessage();
        }
        return;
    }

    const retryGetToken = () => {
        // Update number of retries
        let retryCount = Number(sessionStorage.getItem(CONSTANTS.SESSION_STORAGE_FIT_AUTH_AUTHCODE_RETRY_COUNT) || '0');
        retryCount += 1;
        sessionStorage.setItem(CONSTANTS.SESSION_STORAGE_FIT_AUTH_AUTHCODE_RETRY_COUNT, retryCount.toString());

        if (retryCount <= CONSTANTS.MAX_AUTHCODE_RETRIES) {
            logger.error(`Failed to get token from FIT retry: ${retryCount}`);
            authMetricPublisher.publishCounterMonitor('fit-get-token.ERROR.retry', 1);
            setSubheader(t('retrying-attempt-of-max-attempts',
                {attempt: retryCount, maxAttempts: CONSTANTS.MAX_AUTHCODE_RETRIES}));
            getToken();
        } else {
            logger.error(`Failed to get token from FIT after ${retryCount} retries`);
            authMetricPublisher.publishCounterMonitor('fit-get-token.ERROR.retry.exhausted', 1);
            showErrorMessage();
        }
    }

    /**
     * This hides the spinner and shows the error message in its place.
     */
    const showErrorMessage = () => {
        setSubheader(t('retrying-attempts-exhausted-subheader'));
        setDescription(t('retrying-attempts-exhausted-description'));
        setDisplaySpinner(false);
    }


    return (
        <>
            <Modal
                open={true}
                header={t('authenticating')}
                headerDescription={subheader} // Translated when settting the message
            >
                <div className="row justify-content-center">
                    {displaySpinner && <Spinner size="lg"/>}

                    {/*This is displayed when the spinner is hidden*/}
                    {!displaySpinner && <>{description}</>}
                </div>
            </Modal>
        </>
    );
}
